<template>
  <DropdownMenuCheckboxItem
    v-if="!context.isMobile"
    :model-value="checked"
    as-child
    :disabled="props.disabled"
    @update:model-value="checked = $event"
    @select="onSelect"
  >
    <button
      ref="buttonRef"
      type="button"
      :disabled="props.disabled"
      :class="buttonClass"
    >
      <slot v-if="slots.icon" name="icon" />
      <component
        :is="iconMappingMicro[props.icon]"
        v-else-if="props.icon"
        class="size-4 shrink-0 text-menu-muted-foreground transition-colors"
      />

      <span class="min-w-0 grow break-words">{{ props.label }}</span>

      <BoltIcon
        v-if="premium"
        class="size-4 shrink-0 text-menu-muted-foreground transition-colors"
      />

      <template v-if="checked">
        <CheckIcon
          v-if="variant === 'check'"
          class="size-4 shrink-0 text-blue-400 transition-colors"
        />
        <div
          v-else-if="variant === 'radio'"
          class="grid size-4 shrink-0 place-items-center"
        >
          <div class="size-2 rounded-full bg-blue-400"></div>
        </div>
      </template>
      <div v-else-if="!premium" class="size-4 shrink-0"></div>

      <DropIndicator :edge="draggable.edge" />
    </button>
  </DropdownMenuCheckboxItem>

  <DrawerClose
    v-else-if="props.closeOnSelect"
    as-child
    @click="checked = !checked"
  >
    <button
      ref="buttonRef"
      type="button"
      :disabled="props.disabled"
      :class="buttonClass"
    >
      <slot v-if="slots.icon" name="icon" />
      <component
        :is="iconMappingMicro[props.icon]"
        v-else-if="props.icon"
        class="size-4 shrink-0 text-menu-muted-foreground transition-colors"
      />

      <span class="min-w-0 grow break-words">{{ props.label }}</span>

      <BoltIcon
        v-if="premium"
        class="size-4 shrink-0 text-menu-muted-foreground transition-colors"
      />

      <template v-if="checked">
        <CheckIcon
          v-if="variant === 'check'"
          class="size-4 shrink-0 text-blue-400 transition-colors"
        />
        <div
          v-else-if="variant === 'radio'"
          class="grid size-4 shrink-0 place-items-center"
        >
          <div class="size-2 rounded-full bg-blue-400"></div>
        </div>
      </template>
      <div v-else-if="!premium" class="size-4 shrink-0"></div>

      <DropIndicator :edge="draggable.edge" />
    </button>
  </DrawerClose>

  <button
    v-else
    ref="buttonRef"
    type="button"
    :disabled="props.disabled"
    :class="buttonClass"
    @click="checked = !checked"
  >
    <slot v-if="slots.icon" name="icon" />
    <component
      :is="iconMappingMicro[props.icon]"
      v-else-if="props.icon"
      class="size-4 shrink-0 text-menu-muted-foreground transition-colors"
    />

    <span class="min-w-0 grow break-words">{{ props.label }}</span>

    <BoltIcon
      v-if="premium"
      class="size-4 shrink-0 text-menu-muted-foreground transition-colors"
    />

    <template v-if="checked">
      <CheckIcon
        v-if="variant === 'check'"
        class="size-4 shrink-0 text-blue-400 transition-colors"
      />
      <div
        v-else-if="variant === 'radio'"
        class="grid size-4 shrink-0 place-items-center"
      >
        <div class="size-2 rounded-full bg-blue-400"></div>
      </div>
    </template>
    <div v-else-if="!premium" class="size-4 shrink-0"></div>

    <DropIndicator :edge="draggable.edge" />
  </button>
</template>

<script setup lang="ts">
import { DropdownMenuCheckboxItem } from "reka-ui";
import { DrawerClose } from "vaul-vue";
import { computed, ref } from "vue";

import BoltIcon from "@/icons/heroicons/bolt-micro.svg";
import CheckIcon from "@/icons/heroicons/check-micro.svg";
import {
  type MappedIconMicro,
  iconMappingMicro,
} from "@/icons/icon-mapping-micro";
import { useReorderableListItem } from "@/lib/pragmatic-dnd";
import DropIndicator from "@/lib/pragmatic-dnd/DropIndicator.vue";

import { inject } from "./context";

const context = inject();

const checked = defineModel<boolean>({ default: false });

const props = withDefaults(
  defineProps<{
    label: string;
    icon?: MappedIconMicro;
    premium?: boolean;
    disabled?: boolean;
    variant?: "check" | "radio";
    index?: number;
    closeOnSelect?: boolean;
  }>(),
  {
    variant: "check",
    closeOnSelect: true,
  }
);

const buttonRef = ref<HTMLButtonElement>();

const draggable = useReorderableListItem({
  element: buttonRef,
  index: () => props.index ?? -1,
});

const slots = defineSlots<{
  icon?(): unknown;
}>();

const buttonClass = computed(() => [
  "group/item",
  "relative flex shrink-0 cursor-pointer select-none items-center gap-3 self-stretch rounded-md border-none px-3 outline-none",
  "text-left text-sm text-current",
  !context.isMobile ? "py-1" : "py-2",
  props.premium && !props.disabled && "opacity-50",
  props.disabled && "pointer-events-none cursor-not-allowed opacity-50",
  "active:bg-menu-muted data-[highlighted]:bg-menu-muted",
]);

const onSelect = (event: Event) => {
  if (!props.closeOnSelect) {
    event.preventDefault();
  }
};
</script>
